/* FileUploadUtility.js */

"use strict";

const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");
const tus = require("tus-js-client");

/**
 * Generic file upload utility using TUS protocol.
 * Extends EventEmitter to provide event-based upload tracking.
 *
 * Events:
 * - 'UploadProgress': (bytesUploaded, bytesTotal, percentage) - Emitted during upload progress
 * - 'ChunkComplete': (chunkSize, bytesAccepted, bytesTotal) - Emitted when a chunk completes
 * - 'UploadSuccess': (result) - Emitted when upload completes successfully
 * - 'UploadError': (error) - Emitted when upload fails
 * - 'UploadAborted': (result) - Emitted when upload is aborted
 *
 * @example
 * const uploader = new FileUploadUtility({
 *     file: selectedFile,
 *     metadata: {
 *         username: 'user@example.com',
 *         filename: 'document.pdf',
 *         filetype: 'binary',
 *         destination: 'odm',
 *         organizationId: 'org-123',
 *         instructionId: 'inst-456'
 *     },
 *     endpoint: '/gfs/uploads/',
 *     logger: logger
 * });
 *
 * uploader.on('UploadProgress', (bytesUploaded, bytesTotal, percentage) => {
 *     console.log(`Upload progress: ${percentage}%`);
 * });
 *
 * uploader.on('UploadSuccess', (result) => {
 *     console.log('Upload completed successfully');
 * });
 *
 * uploader.on('UploadError', (error) => {
 *     console.error('Upload failed:', error);
 * });
 *
 * // Start upload
 * uploader.start();
 *
 * // Abort upload if needed
 * uploader.abort();
 */
class FileUploadUtility extends EventEmitter {
    /**
     * Creates a new file upload utility instance.
     *
     * @param {Object} config - Upload configuration
     * @param {File} config.file - The file to upload
     * @param {Object} config.metadata - Metadata to send with the upload
     * @param {string} config.metadata.username - Username performing the upload
     * @param {string} config.metadata.filename - Name of the file
     * @param {string} config.metadata.filetype - Type of file (e.g., 'binary')
     * @param {string} config.metadata.destination - Destination identifier (e.g., 'odm')
     * @param {string} config.metadata.organizationId - Organization ID
     * @param {string} [config.endpoint='/gfs/uploads/'] - Upload endpoint URL
     * @param {number} [config.chunkSize=3000000] - Size of upload chunks in bytes (default 3MB)
     * @param {boolean} [config.resume=false] - Whether to resume interrupted uploads
     * @param {Array<number>} [config.retryDelays] - Retry delays in milliseconds
     * @param {Object} config.logger - Logger instance
     */
    constructor(config) {
        super();

        this._validateConfig(config);

        this._file = config.file;
        this._metadata = config.metadata;
        this._endpoint = config.endpoint || "/gfs/uploads/";
        this._chunkSize = config.chunkSize || 3e6; // 3MB default
        this._resume = config.resume !== undefined ? config.resume : false;
        this._retryDelays = config.retryDelays || [1e2, 8e2, 27e2, 64e2, 125e2]; // Default retry delays
        this._logger = config.logger;

        // Upload state
        this._upload = null;
        this._uploadPromise = null;
        this._uploadResolve = null;
        this._uploadReject = null;
        this._isUploading = false;
        this._isAborted = false;
        this._uploadStartTime = null;

        this._createUploader();
    }

    /**
     * Validates the configuration object.
     * @private
     */
    _validateConfig(config) {
        if (!config) {
            throw new Error("FileUploadUtility: config is required");
        }
        if (!config.file) {
            throw new Error("FileUploadUtility: config.file is required");
        }
        if (!config.metadata) {
            throw new Error("FileUploadUtility: config.metadata is required");
        }
        if (!config.logger) {
            throw new Error("FileUploadUtility: config.logger is required");
        }
    }

    /**
     * Creates the TUS uploader instance.
     * @private
     */
    _createUploader() {
        this._uploadPromise = new Promise((resolve, reject) => {
            this._uploadResolve = resolve;
            this._uploadReject = reject;
        });

        this._upload = new tus.Upload(this._file, {
            endpoint: this._endpoint,
            retryDelays: this._retryDelays,
            metadata: this._metadata,
            chunkSize: this._chunkSize,
            resume: this._resume,
            onError: (error) => {
                this._isUploading = false;
                this._logger.warn(
                    {error, metadata: this._metadata},
                    "FileUploadUtility: Upload error"
                );
                this.emit("UploadError", error);
                this._uploadReject(error);
            },
            onProgress: (bytesUploaded, bytesTotal) => {
                const percentage = bytesTotal > 0 ? (bytesUploaded / bytesTotal) * 100 : 0;
                this.emit("UploadProgress", bytesUploaded, bytesTotal, percentage);
            },
            onChunkComplete: (chunkSize, bytesAccepted, bytesTotal) => {
                this.emit("ChunkComplete", chunkSize, bytesAccepted, bytesTotal);
            },
            onSuccess: () => {
                this._isUploading = false;
                const result = {
                    file: this._file,
                    metadata: this._metadata,
                    duration: Date.now() - this._uploadStartTime,
                };
                this._logger.info(
                    {metadata: this._metadata, duration: result.duration},
                    "FileUploadUtility: Upload completed successfully"
                );
                this.emit("UploadSuccess", result);
                this._uploadResolve({
                    success: true,
                    file: this._file,
                    metadata: this._metadata,
                });
            },
        });
    }

    /**
     * Starts the file upload.
     * @returns {Promise} Promise that resolves when upload completes or rejects on error
     */
    start() {
        if (this._isUploading) {
            this._logger.warn("FileUploadUtility: Upload already in progress");
            return this._uploadPromise;
        }

        if (this._isAborted) {
            return Promise.reject({
                aborted: true,
                message: "Upload was previously aborted",
            });
        }

        this._isUploading = true;
        this._uploadStartTime = Date.now();

        this._logger.info(
            {
                filename: this._file.name,
                filesize: this._file.size,
                metadata: this._metadata,
            },
            "FileUploadUtility: Starting upload"
        );

        try {
            this._upload.start();
        } catch (error) {
            this._isUploading = false;
            this._logger.error({error}, "FileUploadUtility: Failed to start upload");
            this.emit("UploadError", error);
            this._uploadReject(error);
        }

        return this._uploadPromise;
    }

    /**
     * Aborts the current upload.
     * @param {boolean} [shouldTerminate=true] - Whether to terminate the upload on the server
     * @returns {Promise} Promise that resolves when abort is complete
     */
    abort(shouldTerminate = true) {
        if (!this._isUploading && !this._upload) {
            this._logger.warn("FileUploadUtility: No active upload to abort");
            return Promise.resolve({aborted: false, reason: "No active upload"});
        }

        this._isAborted = true;
        this._isUploading = false;

        this._logger.info(
            {metadata: this._metadata, shouldTerminate},
            "FileUploadUtility: Aborting upload"
        );

        const result = {
            aborted: true,
            file: this._file,
            metadata: this._metadata,
        };

        try {
            if (this._upload) {
                this._upload.abort(shouldTerminate);
            }
            this.emit("UploadAborted", result);
            this._uploadReject({
                aborted: true,
                message: "Upload aborted by user",
            });
        } catch (error) {
            this._logger.warn({error}, "FileUploadUtility: Error during abort");
        }

        return Promise.resolve(result);
    }

    /**
     * Gets the current upload progress information.
     * @returns {Object} Progress information
     */
    getProgress() {
        if (!this._upload || !this._upload._offset) {
            return {
                bytesUploaded: 0,
                bytesTotal: this._file.size,
                percentage: 0,
                isUploading: this._isUploading,
            };
        }

        const bytesUploaded = this._upload._offset || 0;
        const bytesTotal = this._file.size;
        const percentage = bytesTotal > 0 ? (bytesUploaded / bytesTotal) * 100 : 0;

        return {
            bytesUploaded,
            bytesTotal,
            percentage,
            isUploading: this._isUploading,
            speed: this._calculateSpeed(bytesUploaded),
            estimatedTimeRemaining: this._calculateETA(bytesUploaded, bytesTotal),
        };
    }

    /**
     * Calculates upload speed in bytes per second.
     * @private
     */
    _calculateSpeed(bytesUploaded) {
        if (!this._uploadStartTime || bytesUploaded === 0) {
            return 0;
        }
        const elapsedTime = Date.now() - this._uploadStartTime;
        return elapsedTime > 0 ? (bytesUploaded / elapsedTime) * 1000 : 0;
    }

    /**
     * Calculates estimated time remaining in milliseconds.
     * @private
     */
    _calculateETA(bytesUploaded, bytesTotal) {
        const speed = this._calculateSpeed(bytesUploaded);
        if (speed === 0 || bytesUploaded === 0) {
            return null;
        }
        const remainingBytes = bytesTotal - bytesUploaded;
        return (remainingBytes / speed) * 1000;
    }

    /**
     * Gets the file being uploaded.
     * @returns {File} The file object
     */
    getFile() {
        return this._file;
    }

    /**
     * Gets the upload metadata.
     * @returns {Object} The metadata object
     */
    getMetadata() {
        return this._metadata;
    }

    /**
     * Checks if upload is currently in progress.
     * @returns {boolean} True if uploading
     */
    isUploading() {
        return this._isUploading;
    }

    /**
     * Checks if upload was aborted.
     * @returns {boolean} True if aborted
     */
    isAborted() {
        return this._isAborted;
    }

    /**
     * Cleans up resources and removes all event listeners.
     */
    destroy() {
        if (this._isUploading) {
            this.abort();
        }

        this.removeAllListeners();

        this._upload = null;
        this._uploadPromise = null;
        this._uploadResolve = null;
        this._uploadReject = null;
        this._file = null;
        this._metadata = null;
        this._logger = null;
    }
}

module.exports = FileUploadUtility;
